import React, { useState, useEffect } from 'react';
import { Mail, MailCheck, MailX, Loader } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface EmailActionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (action: 'welcome' | 'reset' | 'none') => Promise<void>;
  isNewClient: boolean;
  clientName: string;
}

export const EmailActionModal: React.FC<EmailActionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isNewClient,
  clientName
}) => {
  const [selectedAction, setSelectedAction] = useState<'welcome' | 'reset' | 'none'>('welcome');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedAction('welcome');
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await onConfirm(selectedAction);
      onClose();
    } catch (error) {
      console.error("Error confirming email action:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSubmitting && onClose()}>
      <DialogContent className="sm:max-w-md z-[10001]" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold">Notification Email</DialogTitle>
              <DialogDescription>
                {isNewClient ? 'Nouveau client' : 'Mise à jour client'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Souhaitez-vous envoyer un email à <strong className="text-slate-900 dark:text-slate-100">{clientName}</strong> ?
          </p>

          <div className="space-y-3">
            {/* Option: Email de bienvenue */}
            <label
              className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedAction === 'welcome'
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300 dark:hover:border-emerald-700'
              }`}
            >
              <input
                type="radio"
                name="email_action"
                value="welcome"
                checked={selectedAction === 'welcome'}
                onChange={(e) => setSelectedAction(e.target.value as 'welcome' | 'reset' | 'none')}
                className="mt-1"
                disabled={isSubmitting}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <MailCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span className="font-semibold text-slate-900 dark:text-slate-100">Email de bienvenue</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Envoyer un email de bienvenue avec les identifiants de connexion
                </p>
              </div>
            </label>

            {/* Option: Email de réinitialisation */}
            <label
              className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedAction === 'reset'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700'
              }`}
            >
              <input
                type="radio"
                name="email_action"
                value="reset"
                checked={selectedAction === 'reset'}
                onChange={(e) => setSelectedAction(e.target.value as 'welcome' | 'reset' | 'none')}
                className="mt-1"
                disabled={isSubmitting}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold text-slate-900 dark:text-slate-100">Email de réinitialisation</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Envoyer un lien de réinitialisation du mot de passe
                </p>
              </div>
            </label>

            {/* Option: Aucun email */}
            <label
              className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                selectedAction === 'none'
                  ? 'border-slate-500 bg-slate-50 dark:bg-slate-800/50'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <input
                type="radio"
                name="email_action"
                value="none"
                checked={selectedAction === 'none'}
                onChange={(e) => setSelectedAction(e.target.value as 'welcome' | 'reset' | 'none')}
                className="mt-1"
                disabled={isSubmitting}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <MailX className="h-4 w-4 text-slate-600 dark:text-slate-400" />
                  <span className="font-semibold text-slate-900 dark:text-slate-100">Aucun email</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Ne pas envoyer d'email pour le moment
                </p>
              </div>
            </label>
          </div>
        </div>

        <DialogFooter className="gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isSubmitting && <Loader className="h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Enregistrement...' : 'Confirmer'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
